/**
 * MessageSearchBar — Ricerca messaggi dentro una chat specifica
 * Con navigazione tra risultati (prev/next) e highlight
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Search, X, ChevronUp, ChevronDown } from 'lucide-react';
import { useTeamChatStore } from '../../lib/stores/teamChatStore';

interface MessageSearchBarProps {
  chatId: string;
  onClose: () => void;
  onNavigateToMessage: (messageId: string) => void;
}

export function MessageSearchBar({ chatId, onClose, onNavigateToMessage }: MessageSearchBarProps) {
  const { searchMessages } = useTeamChatStore();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<{ id: string; content: string; sender_name: string; created_at: string }[]>([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const doSearch = useCallback(async (q: string) => {
    if (!q.trim() || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await searchMessages(chatId, q.trim());
      setResults(res);
      setCurrentResultIndex(0);
      if (res.length > 0) onNavigateToMessage(res[0].id);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [chatId, searchMessages, onNavigateToMessage]);

  const handleChange = useCallback((value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(value), 300);
  }, [doSearch]);

  const goNext = useCallback(() => {
    if (results.length === 0) return;
    const next = (currentResultIndex + 1) % results.length;
    setCurrentResultIndex(next);
    onNavigateToMessage(results[next].id);
  }, [results, currentResultIndex, onNavigateToMessage]);

  const goPrev = useCallback(() => {
    if (results.length === 0) return;
    const prev = (currentResultIndex - 1 + results.length) % results.length;
    setCurrentResultIndex(prev);
    onNavigateToMessage(results[prev].id);
  }, [results, currentResultIndex, onNavigateToMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (e.ctrlKey || e.metaKey) goPrev();
      else goNext();
    }
  }, [onClose, goNext, goPrev]);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  return (
    <div
      className="flex items-center gap-2 px-3 py-2"
      style={{ backgroundColor: '#F7F7F5', borderBottom: '1px solid rgba(55,53,47,0.09)' }}
    >
      <Search className="w-4 h-4 flex-shrink-0" style={{ color: '#9B9A97' }} strokeWidth={1.5} />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={e => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Cerca nei messaggi..."
        className="flex-1 text-sm bg-transparent outline-none"
        style={{ color: '#37352F' }}
      />
      {query && (
        <span className="text-[11px] flex-shrink-0" style={{ color: '#9B9A97' }}>
          {searching ? '...' : results.length > 0 ? `${currentResultIndex + 1}/${results.length}` : '0 risultati'}
        </span>
      )}
      {results.length > 1 && (
        <div className="flex items-center gap-0.5">
          <button onClick={goPrev} className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] transition-colors" style={{ color: '#787774' }}>
            <ChevronUp className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
          <button onClick={goNext} className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] transition-colors" style={{ color: '#787774' }}>
            <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        </div>
      )}
      <button onClick={onClose} className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] transition-colors" style={{ color: '#787774' }}>
        <X className="w-3.5 h-3.5" strokeWidth={1.5} />
      </button>
    </div>
  );
}
